import React, { useCallback } from 'react'
import {
  ChartOptionElement,
  GetReportDetailsQuery,
  GetReportFavoriteDetailsQuery,
  ReportElement as GqlReportElement,
  ReportFavoriteElement,
} from 'gqlTypes'
import {
  Box,
  Checkbox,
  FormControlLabel,
  ListItemText,
  Menu,
  MenuItem,
  Button,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Divider,
  Link,
  Typography,
  Grid,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import InfoIcon from '@mui/icons-material/Info'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import useReportDetails from 'hooks/useReportDetails'
import useChartOptions from 'hooks/useChartOptions'
import {
  usePopupState,
  bindTrigger,
  bindMenu,
} from 'material-ui-popup-state/hooks'
import DownloadIcon from '@mui/icons-material/Download'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import useElementCohortsAndSubgroups from 'hooks/useElementCohortsAndSubgroups'
import { getCohortToUse, getSubgroupToUse } from 'util/getBarChartData'
import { chartOptionAtom, keyForElementIdAndType } from 'recoil/settings'
import { useRecoilValue } from 'recoil'
import ReportElementFavorite from './ReportElementFavorite'
import PdfReportElementSettings from 'components/PdfReport/ElementSettings'

export interface ReportElementSettingsProps {
  reportId: string
  element: GqlReportElement
  report:
    | NonNullable<GetReportDetailsQuery['report']>
    | NonNullable<GetReportFavoriteDetailsQuery['reportWithFavorites']>
  sectionId: string
  noSubgroup?: boolean
  noCohort?: boolean
  noPastResults?: boolean
  view?: string
  viewOptions?: { title: string; value: string }[]
  showHideOptions?: Array<{ key?: string; title?: React.ReactNode }>
  hidden?: string[]
  onChangeSubgroupItem?: (subgroup: string, subgroupItem?: string) => any
  onChangeHidden?: (hidden: string[]) => any
  onChangeView?: (view: string) => any
  onExport?: () => any
  favorites?: boolean
  onUpdateFavorites: () => void
  print?: boolean
}

const ReportElementSettings: React.FC<ReportElementSettingsProps> = ({
  reportId,
  element,
  report,
  sectionId,
  noSubgroup = false,
  noCohort = false,
  noPastResults = false,
  view,
  viewOptions = [],
  showHideOptions,
  hidden = [],
  onChangeSubgroupItem = () => {},
  onChangeHidden = () => {},
  onChangeView = () => {},
  onExport,
  favorites = false,
  print = false,
  onUpdateFavorites,
}) => {
  const {
    cohort,
    subgroup,
    pastResults,
    config,
    onChangeCohort,
    onChangeSubgroup,
    onChangePastResults,
    handleUseDefault,
  } = useChartOptions(
    element.id,
    ChartOptionElement.Element,
    undefined,
    reportId
  )

  const atom = chartOptionAtom(
    keyForElementIdAndType({
      id: element.id,
      type: ChartOptionElement.Element,
      reportId,
    })
  )
  const baseValues = useRecoilValue(atom)
  const hasOptionsSet = Boolean(Object.keys(baseValues).length)

  const parsedContent = JSON.parse(element.content || 'null')
  const { parsedData } = useReportDetails(reportId)
  const keyMeasuresChart = element.contentType === 'KeyMeasuresChart'

  const [elementCohorts, elementSubgroups] = useElementCohortsAndSubgroups(
    element,
    parsedData
  )

  let subgroupValue = 'None'
  if (keyMeasuresChart) {
    subgroupValue = elementSubgroups.includes(config?.subgroup!)
      ? config.subgroup
      : 'None'
  } else {
    subgroupValue = getSubgroupToUse(subgroup || '', elementSubgroups)
  }

  let elementSubgroupItems: string[] = []
  if (keyMeasuresChart) {
    const row = parsedContent.rows[0]
    const dataIdent = row.type === 'toggledBarChart' ? 'series' : 'values'

    elementSubgroupItems =
      (row?.segmentations.filter(
        (subgroup: { name: string; values: any }) =>
          subgroup.name === subgroupValue
      )[0] || {})[dataIdent]?.map((v: { name: string }) => v.name) || []
  }

  const popupState = usePopupState({ variant: 'popover', popupId: 'demoMenu' })

  const handleChangeHidden = useCallback(
    (key: string) => {
      onChangeHidden(
        hidden.includes(key)
          ? hidden.filter((e) => e !== key)
          : [...hidden, key]
      )
    },
    [onChangeHidden, hidden]
  )

  const handleChangeSubgroupItem = useCallback(
    (subgroup: string, subgroupItem?: string) => {
      onChangeSubgroupItem(subgroup, subgroupItem)
    },
    [onChangeSubgroupItem]
  )

  const handleChangeView = useCallback(
    (view: string) => {
      onChangeView(view)
    },
    [onChangeView, view]
  )

  if (print) {
    return (
      <PdfReportElementSettings
        cohort={
          noCohort
            ? undefined
            : getCohortToUse(cohort || '', elementCohorts, true)
        }
        subgroup={noSubgroup ? undefined : subgroupValue}
        subgroupItem={
          noSubgroup
            ? undefined
            : keyMeasuresChart && subgroupValue != 'None'
              ? config?.subgroupItem
              : undefined
        }
        pastResults={noPastResults ? undefined : pastResults ? 'On' : 'Off'}
      />
    )
  }

  return (
    <Box
      px={4}
      py={2}
      sx={{
        backgroundColor: '#F8F8F8',
      }}
    >
      <Grid
        container
        pb={4}
        direction={{
          xs: 'column-reverse',
          sm: 'column-reverse',
          md: 'column-reverse',
          lg: 'row',
          xl: 'row',
        }}
        sx={{ width: '100%' }}
      >
        <Grid item sm={12} lg={11}>
          <Box
            sx={{
              display: 'flex',
              fontSize: '1.2rem',
            }}
          >
            {hasOptionsSet ? (
              <LockIcon fontSize="inherit" color="secondary" />
            ) : (
              <InfoIcon fontSize="inherit" color="primary" />
            )}
            <Typography
              display="inline-block"
              pl={0.5}
              pr={3}
              sx={{
                color: 'rgba(0, 0, 0, 0.6)',
                fontSize: '0.8rem',
                lineHeight: 1.4,
              }}
            >
              {hasOptionsSet
                ? 'Custom settings have been set for this chart. This chart will not update when default settings are changed. '
                : 'Changing these settings will update this chart only. '}
              {hasOptionsSet && (
                <Link
                  sx={{ whiteSpace: 'nowrap' }}
                  component="button"
                  onClick={() => handleUseDefault()}
                >
                  Use Default Settings
                </Link>
              )}
            </Typography>
          </Box>
        </Grid>
        <Grid item sm={12} lg={1}>
          <Box
            display="flex"
            justifyContent={'flex-end'}
            sx={{ marginTop: '-8px' }}
          >
            <ReportElementFavorite
              id={element.id}
              type={ReportFavoriteElement.Element}
              element={element}
              report={report}
              sectionId={sectionId}
              favorites={favorites}
              onUpdate={onUpdateFavorites}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent={'space-between'}
        rowSpacing={2}
        columnSpacing={1}
      >
        <Grid item>
          <Grid container rowSpacing={2} columnSpacing={1}>
            {!noCohort && (
              <Grid item>
                <TextField
                  select
                  id={`${element.id}-cohort-select`}
                  value={getCohortToUse(cohort || '', elementCohorts)}
                  label="Cohort"
                  onChange={(e) =>
                    e.target.value && onChangeCohort(e.target.value)
                  }
                  size="small"
                  sx={{ width: '250px', backgroundColor: 'white' }}
                >
                  {elementCohorts.map(
                    (cohort: { name: string; value: string }) => (
                      <MenuItem value={cohort.value} key={cohort.value}>
                        {cohort.name}
                      </MenuItem>
                    )
                  )}
                  <MenuItem value="None">None</MenuItem>
                </TextField>
              </Grid>
            )}
            {!noSubgroup && (
              <Grid item>
                <TextField
                  select
                  id={`${element.id}-subgroup-select`}
                  value={subgroupValue}
                  label="Subgroup"
                  onChange={(e) =>
                    keyMeasuresChart
                      ? e.target.value &&
                        handleChangeSubgroupItem(e.target.value)
                      : e.target.value && onChangeSubgroup(e.target.value)
                  }
                  size="small"
                  sx={{ width: '250px', backgroundColor: 'white' }}
                >
                  {keyMeasuresChart && (
                    <MenuItem value="None">Overall</MenuItem>
                  )}
                  {elementSubgroups.map((subgroup: string) => (
                    <MenuItem
                      onClick={() =>
                        keyMeasuresChart
                          ? handleChangeSubgroupItem(subgroup)
                          : onChangeSubgroup(subgroup)
                      }
                      value={subgroup}
                      key={subgroup}
                    >
                      {subgroup}
                    </MenuItem>
                  ))}
                  {!keyMeasuresChart && <MenuItem value="None">None</MenuItem>}
                </TextField>
              </Grid>
            )}
            {!noSubgroup && keyMeasuresChart && subgroupValue != 'None' && (
              <Grid item>
                <TextField
                  select
                  id={`${element.id}-subgroup-item-select`}
                  value={config?.subgroupItem}
                  label={subgroupValue}
                  onChange={(e) =>
                    handleChangeSubgroupItem(subgroupValue, e.target.value)
                  }
                  size="small"
                  sx={{ width: '250px', backgroundColor: 'white' }}
                >
                  <MenuItem disabled>{config?.subgroup}</MenuItem>
                  <Divider />
                  {elementSubgroupItems.map((subgroup: string) => (
                    <MenuItem
                      onClick={() =>
                        handleChangeSubgroupItem(subgroupValue, subgroup)
                      }
                      value={subgroup}
                      key={subgroup}
                    >
                      {subgroup}
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem value="Overall">
                    <Box display="flex" alignItems="center">
                      <ArrowBackIcon fontSize="inherit" />
                      <Box pl={1}>Overall</Box>
                    </Box>
                  </MenuItem>
                </TextField>
              </Grid>
            )}
            {showHideOptions && (
              <Grid item>
                <Button
                  startIcon={
                    hidden.length === 0 ? (
                      <ViewColumnIcon />
                    ) : (
                      <FilterAltIcon color="secondary" />
                    )
                  }
                  variant="outlined"
                  {...bindTrigger(popupState)}
                >
                  Show/Hide Data
                </Button>
                <Menu {...bindMenu(popupState)}>
                  <MenuItem
                    onClick={(e: React.MouseEvent) => {
                      if (hidden.length === 0) {
                        const options: string[] = showHideOptions
                          .filter((d) => typeof d.key === 'string')
                          .map((d) => d.key) as string[]
                        onChangeHidden(options)
                      } else {
                        onChangeHidden([])
                      }
                    }}
                  >
                    <Checkbox checked={hidden.length === 0} />
                    <ListItemText>Select All</ListItemText>
                  </MenuItem>
                  {showHideOptions.map(({ key, title }) => (
                    <MenuItem
                      onClick={() => handleChangeHidden(key!)}
                      key={key}
                    >
                      <Checkbox checked={!hidden.includes(key!)} />
                      <ListItemText>{title}</ListItemText>
                    </MenuItem>
                  ))}
                </Menu>
              </Grid>
            )}
            {!noPastResults && (
              <Grid item>
                <FormControlLabel
                  label="Past Results"
                  control={
                    <Switch
                      checked={!!pastResults}
                      onChange={(_e, value) => onChangePastResults(value)}
                    />
                  }
                />
              </Grid>
            )}
            {view && viewOptions.length > 0 && (
              <Grid item>
                <ToggleButtonGroup
                  exclusive
                  onChange={(e, value) => handleChangeView(value)}
                  size="small"
                  value={view}
                  aria-label="toggle view"
                  color="primary"
                >
                  {viewOptions.map((viewOption) => (
                    <ToggleButton
                      key={viewOption.value}
                      value={viewOption.value}
                    >
                      {viewOption.title}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item>
          {onExport && (
            <Grid item sx={{ justifySelf: 'end' }}>
              <Button
                startIcon={<DownloadIcon />}
                onClick={onExport}
                variant="outlined"
              >
                Export
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

export default ReportElementSettings
